import {Badge} from '../reusables/badge';
import {Icon} from '../reusables/icon';
import {Status} from '../reusables/status';
import {CustomerLockingReasonType} from '../../enum/customner-locking-reason.enum';
import {Address} from './address';
import {Bank} from './bank';
import {CustomerType} from './customer-type';
import {CrmMembership} from './crm-membership';
import {Salutations} from './salutations';
import {CustomerTranslations, DateObject} from '../index';

/**
 * Blueprint for the customer entity.
 */
export class Customer {
  public ['@id']: string;
  public id: number;
  public salutation: Salutations;
  public firstName: string;
  public lastName: string;
  public email: string;
  public phone: string;
  public mobile: string;
  public birthday: DateObject;
  public company: string;
  public externalId: string;
  public externalNumber: string;
  public state: State;
  public addresses?: Array<Address> = [];
  public club: string;
  public bankAccounts?: Array<Bank> = [];
  public membership?: CrmMembership;
  public gender: string;
  public title: string;
  public synced: boolean;
  public subscriptionStatus: string;
  public subscriptionDatetime: Date;
  public duplicate: boolean;
  public searchEmail: string;
  public externalAuthorization: string;
  public equalAuthorizationAndEmail: boolean;
  public allowedAccessTixx: boolean;
  public type: string;
  public possibleDuplicateCount: number;
  public membershipIcon: Icon;
  public containsMembershipData: boolean;

  constructor(
    customer: any,
    private t: CustomerTranslations
  ) {
    if (customer) {
      this.id = customer.id;
      this['@id'] = customer['@id'];
      this.salutation = new Salutations(customer.salutation, this.t.salutationsTranslations);
      this.firstName = customer.firstName;
      this.lastName = customer.lastName;
      this.email = customer.email;
      this.phone = customer.phone;
      this.mobile = customer.mobile;
      this.birthday = customer.birthday ? new DateObject(customer.birthday, 'dd.MM.yyyy') : null;
      this.company = customer.company;
      this.externalId = customer.externalId;
      this.externalNumber = customer.externalNumber;
      this.state = customer.state ? new State(customer.state, t) : null;
      this.club = customer.club;
      this.membership = customer.membership ? new CrmMembership(customer.membership, this.t.membershipTranslations) : null;
      this.gender = customer.gender;
      this.title = customer.title;
      this.synced = customer.synced;
      this.subscriptionStatus = customer.subscriptionStatus;
      this.subscriptionDatetime = customer.subscriptionDatetime;
      this.duplicate = customer.duplicate;
      this.searchEmail = customer.searchEmail;
      this.externalAuthorization = customer.externalAuthorization;
      this.equalAuthorizationAndEmail = customer.equalAuthorizationAndEmail;
      this.allowedAccessTixx = customer.allowedAccessTixx;
      this.type = customer.type;
      this.possibleDuplicateCount = customer.possibleDuplicateCount;
      this.containsMembershipData = customer.containsMembershipData;

      for (const address of customer.addresses) {
        this.addresses.push(new Address(address, this.t.addressTranslations, this.t.salutationsTranslations));
      }

      for (const bank of customer.bankAccounts) {
        this.bankAccounts.push(new Bank(bank));
      }

      if (this.containsMembershipData) {
        this.membershipIcon = new Icon('#esf-icon-membership-number', this.t.membershipTranslations.i18nCustomerHasMembership);
      }
    }
  }

  get isEditable(): boolean {
    return !this.isDuplicate && this.hasTixxId;
  }

  get hasTixxId(): boolean {
    return this.externalId !== null;
  }

  get hasEmail(): boolean {
    return this.email !== null;
  }

  get isDuplicate(): boolean {
    return this.duplicate;
  }

  get canAccessTixx(): boolean {
    return this.allowedAccessTixx;
  }

  get isPerson(): boolean {
    return this.type === CustomerType.PERSON;
  }

  get isCompany(): boolean {
    return this.type === CustomerType.COMPANY;
  }

  get externalNumberBadge(): any {
    return this.possibleDuplicateCount ? new Badge(this.externalNumber, this.possibleDuplicateCount) : this.externalNumber;
  }
}

export enum CustomerSearchType {
  BASIC = 'basic',
  ADVANCED = 'advanced',
}

export class State {
  public status: Status;
  public lockingReason: null | { type: string; name: string };

  public statuses: Array<any> = [
    {value: 'active', status: this.t.statusTranslations.i18nActiveStatus, class: 'esf-tag--success'},
    {value: 'locked', status: this.t.statusTranslations.i18nLockedStatus, class: 'esf-tag--alert'}
  ];

  public reasonsList: Array<any> = [
    {value: CustomerLockingReasonType.DEPARTED, status: this.t.statusTranslations.i18nDeparted},
    {value: CustomerLockingReasonType.SUSPICION_OF_FRAUD, status: this.t.statusTranslations.i18nSuspicionOfFraud},
    {value: CustomerLockingReasonType.ENCASHMENT, status: this.t.statusTranslations.i18nEncashment},
    {value: CustomerLockingReasonType.BLACKLIST, status: this.t.statusTranslations.i18nBlacklist},
    {value: CustomerLockingReasonType.STADIUM_BAN, status: this.t.statusTranslations.i18nStadiumBan},
    {value: CustomerLockingReasonType.OTHER_REASON, status: this.t.statusTranslations.i18nOtherReason},
    {value: CustomerLockingReasonType.CUSTOMER_REQUEST, status: this.t.statusTranslations.i18nCustomerRequest},
    {value: CustomerLockingReasonType.UNKNOWN, status: this.t.statusTranslations.i18nUnknown},
    {value: CustomerLockingReasonType.CHARGE_BACK, status: this.t.statusTranslations.i18nChargeBack},
    {value: CustomerLockingReasonType.DIRECT_DEBIT_RECALL, status: this.t.statusTranslations.i18nDirectDebitRecall},
    {value: '', status: this.t.statusTranslations.i18nNoLockingReason},
  ];


  constructor(state, private t: CustomerTranslations) {
    this.status = this.getFullStatus(state.status);
    this.lockingReason = state.lockingReason;
  }

  /**
   * Retrieves the status
   */
  public getFullStatus(status: string): any {
    const fullStatus = this.statuses.find(el => status === el.value);
    return fullStatus ? new Status(fullStatus) : status;
  }

  public get translatedLockingReason(): string {
    const translatedReason = this.reasonsList.find(type => this.lockingReason?.type === type.value);
    if (this.status.value === 'active') {
      return null;
    } else {
      return translatedReason?.value === CustomerLockingReasonType.UNKNOWN ? this.lockingReason.name : translatedReason.status;
    }
  }
}
